import { useCallback, useEffect, useMemo, useState } from "react";
import type { GridLoader, LayerChange, MapRootName, MasterDataModel } from "@fortex/api";
import { memoryLoader } from "@fortex/api";
import { Button, Chip, errorMessage, format, nameOf } from "@fortex/map";
import type { GridColumn } from "@fortex/map";
import { resolveRootId } from "../../api/rootId";
import { useApp, useAuth } from "../../app/context";
import { useLoad } from "../../app/useLoad";
import { features } from "../../auth/store";
import { langIdOf } from "../../i18n";
import { CrudPage } from "../shared/CrudPage";
import type { FieldErrors } from "../shared/CrudPage";
import { LayerStateChip } from "./LayerDataFields";
import type { LayerStateLabels } from "./LayerDataFields";
import { MasterDataForm } from "./MasterDataForm";
import type { ParentOption } from "./MasterDataForm";
import { MasterDataIcon } from "./MasterDataIcon";
import { idNameProblem, indexRows, mapRootOf, masterDataField, newRow, pathNamesOf, toUpdateModel } from "./treeModel";

export type MasterDataPageProps = { root: MapRootName | null };

type UnivObject = { id: number; name: string; value: string | null };

type TreeData = { rows: MasterDataModel[]; rootId: number | null };

export function masterDataColumns(labels: { colIcon: string; colId: string; colName: string; colIdName: string; colValue: string; colValue2: string; colOrder: string; colVisible: string; colParent: string; layerData: string } & LayerStateLabels, langId: number, api: { typeIcon(idName: string): Promise<string | null> }, showValue2: boolean, showLayerState = false): GridColumn<MasterDataModel>[] {
  const nameField = `Name.Lang${langId}Short`;
  const columns: GridColumn<MasterDataModel>[] = [
    { key: "Icon", header: labels.colIcon, width: "48px", render: row => <MasterDataIcon api={api as never} idName={row.idName} hasIcon={row.hasIcon} /> },
    { key: "Id", header: labels.colId, field: "Id", sortable: true, mono: true, width: "72px", filter: { kind: "number" } },
    { key: "Name", header: labels.colName, field: nameField, sortable: true, filter: { kind: "text" }, render: row => nameOf(row, langId) },
    { key: "IdName", header: labels.colIdName, field: "IdName", sortable: true, mono: true, filter: { kind: "text" } },
    { key: "Value", header: labels.colValue, field: "Value", sortable: true, filter: { kind: "text" }, render: row => (row.value ? <Chip tone="neutral">{row.value}</Chip> : null) }
  ];

  if (showLayerState) columns.push({ key: "LayerState", header: labels.layerData, width: "180px", render: row => (row.layer ? <LayerStateChip state={row.layer.state} labels={labels} /> : null) });
  if (showValue2) columns.push({ key: "Value2", header: labels.colValue2, field: "Value2", sortable: true, filter: { kind: "text" }, render: row => <span className="fxapp-cell-clip">{row.value2 ?? ""}</span> });

  columns.push(
    { key: "Order", header: labels.colOrder, field: "Order_", sortable: true, align: "end", width: "88px", filter: { kind: "number" } },
    { key: "Visible", header: labels.colVisible, field: "Visible", sortable: true, width: "96px", filter: { kind: "boolean" }, render: row => <span className={row.visible ? "fxapp-dot fxapp-dot-on" : "fxapp-dot"} aria-hidden="true" /> },
    { key: "Parent", header: labels.colParent, field: "ParentMasterData.IdName", sortable: true, mono: true, filter: { kind: "text" }, render: row => row.parentIdName ?? "" }
  );

  return columns;
}

export function withLayerChange(rows: MasterDataModel[], change: LayerChange): MasterDataModel[] {
  return rows.map(a => (a.id === change.masterDataId ? { ...a, layer: { state: change.state, bounds: change.bounds, hasTable: change.hasTable } } : a));
}

export function MasterDataPage({ root }: MasterDataPageProps) {
  const { api, labels, lang, toast } = useApp();
  const canEdit = useAuth(a => a.can(features.masterData, "Full"));
  const langId = langIdOf(lang);
  const tree = useLoad<TreeData | null>(
    async signal => {
      if (root === null) return null;

      const rows = await api.tree(root, signal);
      const rootId = await resolveRootId(api, root, rows, signal);

      return { rows, rootId };
    },
    [root]
  );
  const univ = useLoad<UnivObject[]>(async signal => (root === null ? api.request<UnivObject[]>(`masterdata/univ/${langId}`, { signal }) : []), [root, langId]);
  const [layerVersions, setLayerVersions] = useState<Record<number, number>>({});
  const [refreshingLayers, setRefreshingLayers] = useState(false);
  const setTreeData = tree.setData;

  useEffect(() => {
    const pending = api
      .connectLayerEvents(change => {
        setTreeData(previous => (previous === null ? previous : { ...previous, rows: withLayerChange(previous.rows, change) }));
        setLayerVersions(previous => ({ ...previous, [change.masterDataId]: (previous[change.masterDataId] ?? 0) + 1 }));
      })
      .catch(() => null);

    return () => void pending.then(handle => handle?.stop()).catch(() => undefined);
  }, [api, setTreeData]);

  const rows = useMemo(() => tree.data?.rows ?? [], [tree.data]);
  const rootId = tree.data?.rootId ?? null;
  const index = useMemo(() => indexRows(rows), [rows]);
  const read = useMemo(() => masterDataField(langId), [langId]);
  const nameFor = useCallback((row: MasterDataModel) => nameOf(row, langId), [langId]);

  const load = useMemo<GridLoader<MasterDataModel>>(() => (root === null ? (query, signal) => api.masterDataList(query, signal) : memoryLoader(rows, read)), [api, root, rows, read]);

  const columns = useMemo(() => masterDataColumns(labels, langId, api, root === null, root !== null), [labels, langId, api, root]);

  const groupBy = useCallback((row: MasterDataModel) => (root === null ? [row.parentIdName ?? labels.rootLevel] : pathNamesOf(row, index, nameFor)), [root, index, nameFor, labels.rootLevel]);

  const parents = useMemo<ParentOption[]>(() => {
    if (root !== null) {
      const options = rows.map(a => ({ id: a.id, label: `${pathNamesOf(a, index, nameFor).concat(nameFor(a)).join(" › ")} (${a.idName})`, idName: a.idName }));

      if (rootId !== null) options.unshift({ id: rootId, label: `${labels.rootLevel} (${root})`, idName: root });

      return options.sort((a, b) => a.label.localeCompare(b.label));
    }

    return (univ.data ?? []).map(a => ({ id: a.id, label: a.name, idName: a.value })).sort((a, b) => a.label.localeCompare(b.label));
  }, [root, rows, index, nameFor, rootId, univ.data, labels.rootLevel]);

  const refreshLayers = async () => {
    if (root === null) return;

    setRefreshingLayers(true);

    try {
      const result = await api.refreshLayers(root);

      toast("success", format(labels.layersRefreshed, { reset: result.reset, skipped: result.skipped }));
      tree.reload();
    } catch (reason) {
      toast("danger", errorMessage(reason, labels));
    } finally {
      setRefreshingLayers(false);
    }
  };

  const validate = (draft: MasterDataModel): FieldErrors => {
    const errors: FieldErrors = {};
    const problem = idNameProblem(draft.idName, root ?? mapRootOf(draft.idName), candidate => rows.some(a => a.id !== draft.id && a.idName.toUpperCase() === candidate.toUpperCase()));

    if (problem === "required") errors.idName = labels.idNameRequired;
    else if (problem === "prefix") errors.idName = labels.idNamePrefix.replace("{prefix}", root === null ? "" : `${root}_`);
    else if (problem === "chars") errors.idName = labels.idNameChars;
    else if (problem === "taken") errors.idName = labels.idNameTaken;

    if ((draft.name?.lang?.[0] ?? "").trim().length === 0) errors.nameHu = labels.nameRequired;
    if (root === null && mapRootOf(draft.idName) === null && (draft.value ?? "").trim().length === 0) errors.value = labels.valueRequired;

    return errors;
  };

  return (
    <CrudPage<MasterDataModel, MasterDataModel>
      dataFxmap={root === null ? "masterdata-page" : root === "MAP" ? "masterdata-maps-page" : "masterdata-layers-page"}
      columns={columns}
      rowKey={row => String(row.id)}
      load={load}
      defaultSortField={root === null ? "IdName" : "Order_"}
      quickFields={["IdName", `Name.Lang${langId}Short`, "Value"]}
      canEdit={canEdit}
      newLabel={labels.newMasterData}
      emptyDraft={() => newRow(root, null, rootId)}
      toDraft={row => (root === null ? api.masterData(row.id) : Promise.resolve(row))}
      detailTitle={editing => (editing.isNew ? labels.newMasterData : labels.editMasterData)}
      detailSubtitle={editing => (editing.isNew ? null : `#${editing.draft.id} · ${editing.draft.idName}`)}
      form={(draft, setDraft, errors) => <MasterDataForm api={api} draft={draft} setDraft={setDraft} errors={errors} labels={labels} parents={parents.filter(a => a.id !== draft.id)} hasChildren={(index.childCount.get(draft.id) ?? 0) > 0} readOnly={!canEdit} layerVersion={layerVersions[draft.id] ?? 0} />}
      validate={validate}
      save={async (draft, isNew) => {
        const model = toUpdateModel(draft);

        if (isNew) await api.createMasterData(model);
        else await api.updateMasterData(draft.id, model);
      }}
      remove={canEdit ? async draft => api.deleteMasterData(draft.id) : null}
      deleteText={draft => labels.deleteMasterDataText.replace("{name}", nameFor(draft) || draft.idName)}
      savedText={labels.nodeSaved}
      deletedText={labels.deleted}
      toolbar={root !== null && canEdit ? <Button kind="ghost" size="sm" icon="redo" label={labels.refreshAllLayers} busy={refreshingLayers} onClick={() => void refreshLayers()} data-fxmap="layers-refresh-all" /> : null}
      groupBy={groupBy}
      groupSwitchLabel={labels.groupByTree}
      exportName={root === null ? "masterdata" : root.toLowerCase()}
      reloadToken={rows.length}
      onReload={root === null ? undefined : tree.reload}
    />
  );
}
